import { createBaseStore } from '@/stores/api/base.js';
import { animationManager } from '@/threejs/managers/animationManager';
import { useRefHistory } from '@vueuse/core';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useTimelineStore = defineStore('timeline', () => {
  const currentTimeline = ref([]);
  const { history, clear, commit, undo, redo } = useRefHistory(currentTimeline, {
    deep: true,
    flush: 'post',
  });

  const { fetchItems: fetch, storeItem: store, updateItem: update } = createBaseStore('timeline');

  const fetchWithHistory = async (itemId = null) => {
    const result = await fetch(itemId);
    if (itemId) {
      currentTimeline.value = result;
      clear();
      commit();

      // Sync with animationManager
      syncTimelineWithAnimationManager();
    }
    return result;
  };

  const groupedKeyframes = ref(new Map());

  const addKeyframe = (objectId, key, time, value) => {
    const layerKey = `${objectId}-${key}`;
    // Find or create the layer
    let layer = currentTimeline.value.find((layer) => layer.id === layerKey);
    if (!layer) {
      layer = {
        id: layerKey,
        objectId,
        key,
        keyframes: [],
      };
      currentTimeline.value.push(layer);
    }

    // Check if a keyframe already exists at this time
    const existingKeyframe = layer.keyframes.find((kf) => kf.time === time);
    if (existingKeyframe) {
      return; // Skip adding if a keyframe already exists at this time
    }

    // Generate unique ID for keyframe
    const keyframeId = `${layerKey}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    layer.keyframes.push({ id: keyframeId, time, value });

    // Sort keyframes by time
    layer.keyframes.sort((a, b) => a.time - b.time);

    // Update animation manager
    updateAnimationTrack(objectId, key);
  };

  const updateKeyframeTime = (layerId, keyframeId, time) => {
    const layer = currentTimeline.value.find((layer) => layer.id === layerId);
    if (layer) {
      // Find keyframe by ID, or by the fallback pattern if ID doesn't exist
      let keyframe = layer.keyframes.find((kf) => kf.id === keyframeId);

      // If not found by ID, try to find by fallback pattern (layerId-time)
      if (!keyframe && keyframeId.includes('-')) {
        const originalTime = parseFloat(keyframeId.split('-').pop());
        keyframe = layer.keyframes.find((kf) => kf.time === originalTime);
      }

      if (keyframe) {
        keyframe.time = time;
        // Add ID if it doesn't exist
        if (!keyframe.id) {
          keyframe.id = `${layerId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        }
        // Re-sort keyframes by time after update
        layer.keyframes.sort((a, b) => a.time - b.time);

        // Update animation manager
        updateAnimationTrack(layer.objectId, layer.key);
      }
    }
  };

  const getGroupedKeyframes = () => {
    return currentTimeline.value;
  };

  const deleteLayer = (layerId) => {
    const index = currentTimeline.value.findIndex((layer) => layer.id === layerId);
    if (index !== -1) {
      const layer = currentTimeline.value[index];
      currentTimeline.value.splice(index, 1);

      // Remove from animation manager
      animationManager.deleteTrack(layer.objectId, layer.key);
    }
  };

  // Helper function to update a single animation track
  const updateAnimationTrack = (objectId, key) => {
    const layer = currentTimeline.value.find((layer) => layer.objectId === objectId && layer.key === key);
  };

  // Sync entire timeline with animation manager using crux modules
  const syncTimelineWithAnimationManager = (cruxModules) => {
    // Clear existing animations
    animationManager.clear();

    // Filter timeline_layer modules and add to animation manager
    const timelineLayers = cruxModules.filter((module) => module.type === 'timeline_layer');
  };

  const hasKeyframe = (objectId, key) => {
    const layer = currentTimeline.value.find((layer) => layer.objectId === objectId && layer.key === key);
    return layer ? layer.keyframes.length > 0 : false;
  };

  const getKeyframeAtTime = (objectId, key, time) => {
    const layer = currentTimeline.value.find((layer) => layer.objectId === objectId && layer.key === key);
    return layer ? layer.keyframes.find((kf) => kf.time === time) : null;
  };

  return {
    fetch: fetchWithHistory,
    currentTimeline,
    groupedKeyframes,
    getGroupedKeyframes,
    addKeyframe,
    updateKeyframeTime,
    deleteLayer,
    hasKeyframe,
    getKeyframeAtTime,
    syncTimelineWithAnimationManager,
    history,
    undo,
    redo,
    store,
    update,
  };
});
